/**
 * Fase 1 del plan white-label-v2 (ver WHITE-LABEL-V2.md §1.3).
 *
 * Deriva la paleta de marca completa a partir de los colores del JSON
 * por dominio (`theme.colors`). Los tokens neutros (superficies, bordes,
 * texto, radios, tipografía) viven en `palette.js` y NO cambian por marca.
 *
 * Contrato de `theme.colors` en el JSON de dominio:
 *
 *   primary    acción principal (botones, links, iconos activos, checks)
 *   secondary  acento secundario de marca -> MUI `palette.secondary.main`
 *   brandDark  superficie oscura de marca (sidebar). OPCIONAL: si no
 *              viene, se deriva oscureciendo `primary`.
 *
 * `secondary` NO pinta el sidebar. Su semántica varía entre tenants
 * (Pinpeo lo manda como verde claro, Pagaqui como negro), así que
 * sobrecargarlo con la superficie oscura rompía la marca de cualquiera
 * que lo mandara claro. La superficie tiene su propio campo.
 *
 * Contrato interno: ningún componente debe importar `PINPEO` para
 * colores de marca; se consumen vía `theme.brand` (makeStyles/useTheme).
 */
import {
  alpha,
  darken,
  getContrastRatio,
  lighten,
} from '@material-ui/core/styles/colorManipulator';

import { PINPEO } from './palette';

const WHITE = '#FFFFFF';
const MIN_CONTRAST = 4.5;    // WCAG AA para texto normal
const SURFACE_DARKEN = 0.55; // cuánto oscurecer `primary` si no hay `brandDark`

/**
 * Guard de contraste: la superficie de marca lleva texto blanco
 * (sidebar). Si llega un color demasiado claro para texto blanco, se
 * oscurece progresivamente hasta cumplir 4.5:1.
 * @param {string} color
 * @returns {string}
 */
function ensureDarkSurface(color) {
  let surface = color;
  let attempts = 0;

  while (getContrastRatio(surface, WHITE) < MIN_CONTRAST && attempts < 20) {
    surface = darken(surface, 0.15);
    attempts += 1;
  }

  return surface;
}

/**
 * Resuelve la superficie oscura de marca:
 *   1. `colors.brandDark` si el tenant lo declara,
 *   2. si no, se deriva de `colors.primary`,
 *   3. si no hay colores (fetch fallido), la marca Pinpeo.
 * En los tres casos pasa por el guard de contraste.
 * @param {{ primary?: string, brandDark?: string }} colors
 * @returns {string}
 */
function resolveSurface(colors) {
  if (colors?.brandDark) {
    return ensureDarkSurface(colors.brandDark);
  }

  if (colors?.primary) {
    return ensureDarkSurface(darken(colors.primary, SURFACE_DARKEN));
  }

  return ensureDarkSurface(PINPEO.brandDark);
}

/**
 * Crea los tokens de marca derivados desde `theme.colors` del JSON
 * de dominio. Con `{}` (o fetch fallido) devuelve la marca Pinpeo.
 * @param {{ primary?: string, secondary?: string, brandDark?: string }} colors
 */
export function createBrandTokens(colors = {}) {
  const primary = colors?.primary || PINPEO.primary;

  return {
    // Base
    primary,
    // Acento de marca: cae a `primary` si el tenant no lo declara.
    secondary: colors?.secondary || primary,
    // Superficie oscura (sidebar), independiente de `secondary`.
    brandDark: resolveSurface(colors),

    // Derivados de acción
    primaryHover: darken(primary, 0.18),
    primarySoft: lighten(primary, 0.92),

    // Filas de tabla (tintes 4–8% del primario)
    rowHover: alpha(primary, 0.06),
    rowSelected: alpha(primary, 0.08),
    rowStripe: alpha(primary, 0.04),

    // Fondo de chips/segmentos
    track: lighten(primary, 0.88),

    // Acento claro para iconografía sobre superficies oscuras (sidebar)
    accentOnDark: lighten(primary, 0.55),
  };
}

/**
 * Singleton mutable con los tokens de la marca activa. Se sincroniza en
 * createTheme() (theme/index.js), que corre ANTES de montar las vistas
 * (lazy) — ver el gate de settings en App.jsx.
 *
 * En componentes usar SIEMPRE `theme.brand` (makeStyles/useTheme);
 * BRAND existe para helpers de render fuera del árbol de React
 * (builders de celdas/columnas, funciones de estilo, etc.).
 */
export const BRAND = createBrandTokens();

export function applyBrandTokens(tokens) {
  return Object.assign(BRAND, tokens);
}

export default createBrandTokens;
